import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LiveChat = ({ t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [lastTimestamp, setLastTimestamp] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const isNL = t.nav?.services === 'Diensten';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for admin replies
  const pollReplies = useCallback(async () => {
    if (!hasStarted) return;
    try {
      const url = `${BACKEND_URL}/api/homepage/chat/${sessionId}?after=${encodeURIComponent(lastTimestamp)}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          const newMsgs = (Array.isArray(data.messages) ? data.messages : []).map((m) => ({ type: 'bot', text: m.message }));
          setMessages((prev) => [...prev, ...newMsgs]);
          setLastTimestamp(data.messages[data.messages.length - 1].created_at);
        }
      }
    } catch {
      // silent
    }
  }, [hasStarted, sessionId, lastTimestamp]);

  useEffect(() => {
    if (hasStarted && isOpen) {
      pollRef.current = setInterval(pollReplies, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [hasStarted, isOpen, pollReplies]);

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 200);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = ((inputValue) == null ? '' : String(inputValue)).trim();
    if (!text || sending) return;

    setMessages((prev) => [...prev, { type: 'user', text }]);
    setInputValue('');
    setSending(true);

    try {
      await fetch(`${BACKEND_URL}/api/homepage/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!hasStarted) {
        setHasStarted(true);
        setMessages((prev) => [
          ...prev,
          {
            type: 'bot',
            text: isNL
              ? 'Bedankt voor uw bericht! Een van onze medewerkers neemt zo snel mogelijk contact met u op.'
              : 'Thank you for your message! One of our team members will get back to you as soon as possible.',
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          text: isNL
            ? 'Er is iets misgegaan. Probeer het later opnieuw.'
            : 'Something went wrong. Please try again later.',
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
        className="chat-toggle"
        aria-label="Toggle chat"
        data-testid="whatsapp-chat-btn"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {isOpen && (
        <div className="chat-widget" data-testid="chat-widget">
          <div className="chat-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <h4 className="heading-3" style={{ margin: 0 }}>Chat</h4>
            </div>
            <button onClick={() => setIsOpen(false)} className="chat-close" data-testid="chat-close-btn">
              <X size={20} />
            </button>
          </div>

          <div className="chat-messages">
            <div className="chat-message bot">
              <div className="message-bubble body-small">{t.chat.greeting}</div>
            </div>
            <div className="chat-message bot">
              <div className="message-bubble body-small" style={{ opacity: 0.85 }}>
                {isNL
                  ? 'Typ uw bericht en klik op versturen. U wordt doorverbonden met een van onze Support medewerkers.'
                  : 'Type your message and click send. You will be redirected to one of our Support members.'}
              </div>
            </div>

            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`chat-message ${msg.type === 'user' ? 'user' : 'bot'}`}>
                <div className="message-bubble body-small">{msg.text}</div>
              </div>
            ))}

            {sending && (
              <div className="chat-message bot">
                <div className="message-bubble body-small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Loader size={14} className="spin" /> {isNL ? 'Verzenden...' : 'Sending...'}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSend} className="chat-input-form" data-testid="chat-form">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t.chat.placeholder}
              className="chat-input"
              disabled={sending}
              data-testid="chat-input"
            />
            <button type="submit" className="chat-send" disabled={sending || !((inputValue) == null ? '' : String(inputValue)).trim()} data-testid="chat-send-btn">
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default LiveChat;
